import {
  Badge,
  Box,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast
} from '@chakra-ui/react'
import { FC, useState } from 'react'
import { MdUpload } from 'react-icons/md'

import FuModalHeader from '@component/FollowUpFornecedor/ModalHeader'

import followUpFornecedorService from '@service/followUpFornecedor'

export interface IImportRow {
  supplierId: number
  fornecedorNome: string
  categoriaCompactaId: number | null
  categoriaCompactaNome: string | null
  email: string
}

interface Props {
  isOpen: boolean
  rows: IImportRow[]
  onClose: () => void
  onSuccess: () => void
}

const ImportModal: FC<Props> = ({ isOpen, rows, onClose, onSuccess }) => {
  const toast = useToast()
  const [importing, setImporting] = useState(false)

  const handleConfirm = async () => {
    setImporting(true)
    try {
      const itens = rows.map(r => ({
        supplierId: r.supplierId,
        compactCategoryId: r.categoriaCompactaId,
        userEmail: r.email
      }))
      await followUpFornecedorService.createResponsaveis({ itens })
      toast({
        title: `${rows.length} registro(s) importado(s) com sucesso`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      onSuccess()
      onClose()
    } catch (err: any) {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao importar',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setImporting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
      motionPreset="slideInBottom"
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent>
        <FuModalHeader
          icon={MdUpload}
          title={`Importar CSV — ${rows.length} registro${
            rows.length !== 1 ? 's' : ''
          }`}
        />
        <ModalCloseButton color="white" />

        <ModalBody bg="white" py={4}>
          <Text fontSize="13px" color="gray.500" mb={3}>
            Revise os registros abaixo antes de confirmar a importação.
          </Text>
          <Box
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
          >
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Fornecedor ID</Th>
                  <Th>Fornecedor</Th>
                  <Th>Categoria Compacta</Th>
                  <Th>E-mail</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row, idx) => (
                  <Tr key={idx}>
                    <Td fontSize="12px" color="gray.500">
                      {row.supplierId}
                    </Td>
                    <Td fontSize="13px" fontWeight="500">
                      {row.fornecedorNome || (
                        <Text as="span" color="gray.400">
                          —
                        </Text>
                      )}
                    </Td>
                    <Td>
                      {row.categoriaCompactaId ? (
                        <Badge
                          colorScheme="blue"
                          borderRadius="full"
                          fontSize="11px"
                          px={2}
                        >
                          {row.categoriaCompactaNome ||
                            `ID ${row.categoriaCompactaId}`}
                        </Badge>
                      ) : (
                        <Badge
                          colorScheme="green"
                          borderRadius="full"
                          fontSize="11px"
                          px={2}
                        >
                          Todos os produtos
                        </Badge>
                      )}
                    </Td>
                    <Td fontSize="13px">{row.email}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </ModalBody>

        <ModalFooter
          bg="white"
          borderBottomLeftRadius={5}
          borderBottomRightRadius={5}
        >
          <Button
            variant="outline"
            mr={3}
            onClick={onClose}
            isDisabled={importing}
          >
            Cancelar
          </Button>
          <Button
            bg="#1d5840"
            color="white"
            _hover={{ bg: '#174a35' }}
            leftIcon={<Icon as={MdUpload} />}
            isLoading={importing}
            loadingText="Importando..."
            onClick={handleConfirm}
          >
            Confirmar Importação
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ImportModal
